import * as React from 'react';
import CreateSection from './CreateSection';
import CreateField from './CreateField';

import * as IDatabase from '../../../types/database';
import { History } from 'history';
import { InjectedIntlProps, injectIntl, defineMessages, FormattedMessage, FormattedHTMLMessage } from 'react-intl';

// tslint:disable-next-line: no-var-requires
const moment = require('moment')

export interface IState {
    currentSectionIndex: number;
    sections: IDatabase.ISection[];
    selected: IDatabase.ISection[];
    edit: { field: IDatabase.IField; sectionId: number } | null;
    saved?: boolean;
}
interface IProps {
    defaultFields: IDatabase.ISection[];
    handleSave: (state: IState) => void;
    getSavedSession: () => Promise<{ fields: IState } | null>;
    renderNextStepLink: (onClick: () => boolean) => any;
    history: History;
}

const customFieldModalId = 'customFieldModal';
const editFieldModalId = 'editFieldModal';
const customSectionModalId = 'customSectionModal';

const extractFixedSection = (sections: IDatabase.ISection[]): IDatabase.ISection[] => {
    const fixedFields: IDatabase.ISection[] = []
    sections.forEach(section => {
        const fields: IDatabase.IField[] = []
        section.fields.forEach(field => field.fixed ? fields.push(field) : null)
        if (fields.length) fixedFields.push({
            ...section,
            fields
        })
    })
    return fixedFields
};

class Fields extends React.Component<IProps & InjectedIntlProps, IState> {
    state: IState = {
        currentSectionIndex: 0,
        selected: extractFixedSection(this.props.defaultFields),
        sections: this.props.defaultFields,
        edit: null,
        saved: false,
    };

    messages = defineMessages({
        updateError: {
            id: 'db.fields.update.error',
            defaultMessage: 'Error updating selected field. Please try again.'
        },
        deleteSection: {
            id: 'db.fields.delete.section.tooltip',
            defaultMessage: 'Remove section'
        },
        deleteField: {
            id: 'db.fields.delete.field.tooltip',
            defaultMessage: 'Remove field'
        },
        editField: {
            id: 'db.fields.edit.field.btn',
            defaultMessage: 'Edit field'
        },
        sectionHelp: {
            id: 'db.fields.sections.help',
            defaultMessage: 'Similar fields have been grouped together into "sections" to help categorize them and for display on Information Pages. You can use the suggested sections or create your own custom sections.'
        },
        fieldsHelp: {
            id: 'db.fields.fields.help',
            defaultMessage: 'A field is a database structure that contains a single piece of data.  Fields are organized into records, which contain all the information relevant to a specific entity or resource. In Harmonize®, records are viewed as Information Pages, which provide information on a specific organization, resource or other topic, as defined by the account owner.'
        }
    });

    async componentDidMount() {
        const session = await this.props.getSavedSession();
        if (session && session.fields) {
            this.setState({
                ...session.fields,
                selected: session.fields.selected.sort((a, b) => (a.order || 0) - (b.order || 0)),
                sections: session.fields.sections.length ? session.fields.sections : this.props.defaultFields
            });
        }
    }

    handleGoBack = () => {
        this.props.history.goBack();
    };

    handleSave = () => {
        this.props.handleSave({ ...this.state });
        this.setState({ saved: true }, () => {
            setTimeout(() => {
                this.setState({ saved: false })
            }, 2000)
        })
        return true;
    };

    addSection = (name: string, index?: number): Promise<void> => {
        window.onbeforeunload = () => {
            return true;
        };
        const sections = [
            ...this.state.sections,
            { name, fields: [], id: this.state.sections.length + 1, isDefault: false, order: index || this.state.sections.length },
        ];
        return new Promise<void>((resolve: () => void) => {
            this.setState({ sections }, () => {
                this.setState({ currentSectionIndex: sections.length - 1 });
                resolve();
            });
        });
    };

    addField = (field: IDatabase.IField): Promise<void> => {
        window.onbeforeunload = () => {
            return true;
        };
        const sections = [...this.state.sections];
        field.id = `${Math.random() - moment()}`
        const fields = [...sections[this.state.currentSectionIndex].fields, { ...field, order: sections[this.state.currentSectionIndex].fields.length }];
        sections[this.state.currentSectionIndex].fields = fields;
        return new Promise<void>((resolve: () => void) => {
            this.setState({ sections }, () => {
                this.handleFieldSelected(field);
                resolve();
            });
        });
    };

    editField = (field: IDatabase.IField): Promise<void> => {
        window.onbeforeunload = () => {
            return true;
        };
        const selected = [...this.state.selected];
        if (this.state.edit !== null) {
            const sectionIndex = selected.findIndex(
                x => this.state.edit !== null && x.id === this.state.edit.sectionId
            );
            if (sectionIndex !== -1) {
                const fieldIndex = selected[sectionIndex].fields.findIndex(
                    x => this.state.edit !== null && x.name === this.state.edit.field.name
                );
                if (fieldIndex !== -1) {
                    selected[sectionIndex].fields[fieldIndex] = { ...field };
                    return new Promise<void>((resolve: () => void) => {
                        this.setState({ selected }, resolve);
                    });
                }
            }
        }
        return new Promise<void>((resolve: () => void, reject: () => void) => {
            alert(this.props.intl.formatMessage(this.messages.updateError));
            reject();
        });
    };

    handleFieldSelected = (field: IDatabase.IField) => {
        window.onbeforeunload = () => {
            return true;
        };
        const selected = [...this.state.selected];
        if (this.state.currentSectionIndex === null) return;
        const section = this.state.sections[this.state.currentSectionIndex];
        if (section) {
            const alreadySelectedSection = selected.find(x => x.id === section.id);
            if (alreadySelectedSection) {
                if (alreadySelectedSection.fields.findIndex(x => x.name === field.name) === -1)
                    alreadySelectedSection.fields.push({ ...field, order: alreadySelectedSection.fields.length });
            } else {
                selected.push({ ...section, fields: [{ ...field, order: 0 }], order: selected.length });
            }
            this.setState({ selected });
        }
    };

    handleRemoveSelectedSection = (sectionId: number) => {
        window.onbeforeunload = () => {
            return true;
        };
        const selected = [...this.state.selected];
        const updatedSelected = selected.filter(x => x.id !== sectionId);
        this.setState({ selected: updatedSelected });
    };

    handleRemoveSelectedField = (field: IDatabase.IField, sectionId: number, i: number) => {
        window.onbeforeunload = () => {
            return true;
        };
        let selected = [...this.state.selected];
        const section = selected.find(x => x.id === sectionId);
        if (section) {
            if (section.fields[i] !== field) return;
            section.fields.splice(i, 1);
            if (!section.fields.length) {
                selected = selected.filter(x => x.id !== sectionId);
            }
            this.setState({ selected });
        }
    };

    handleEditField = (field: IDatabase.IField, sectionId: number) => {
        this.setState({ edit: { field, sectionId } });
    };

    renderCustomSection = (section: IDatabase.ISection, index: number) => {
        if (!section.isDefault) {
            return this.renderSection(section, index);
        }
        return null;
    };

    renderDefaultSection = (section: IDatabase.ISection, index: number) => {
        if (section.isDefault) {
            return this.renderSection(section, index);
        }
        return null;
    };

    renderSection = (section: IDatabase.ISection, index: number) => {
        return (
            <li
                key={`section-${index}`}
                className={index === this.state.currentSectionIndex ? 'active' : ''}>
                <span
                    className="p-3"
                    onClick={() => {
                        this.setState({ currentSectionIndex: index });
                    }}>
                    {section.name}
                </span>
            </li>
        );
    };

    renderSelectedField = (i: number, field: IDatabase.IField, section: IDatabase.ISection) => {
        return (
            <div className="row p-3 border" key={`${field.name}-${i}`}>
                <div className="col-lg-6 col-md-4 col-sm-4">
                    {field.label}
                    <div className="font-weight-light">
                        <small>{field.type}</small>
                    </div>
                </div>
                <div className="col-lg-2 col-md-3 col-sm-4 text-align-right">
                    {field.isRequired ? (
                        <FormattedMessage id="db.fields.required" defaultMessage="Required">
                            {(txt) => (<span className="badge badge-secondary float-sm-right">{txt}</span>)}
                        </FormattedMessage>
                    ) : (
                            ''
                        )}
                </div>
                {!field.fixed &&
                    <div className="col-lg-4 col-md-5 col-sm-4">
                        <button
                            className="btn btn-outline-danger float-sm-right"
                            onClick={() => this.handleRemoveSelectedField(field, section.id, i)}
                            title={this.props.intl.formatMessage(this.messages.deleteField)}>
                            <i className="fas fa-trash-alt" />
                        </button>
                        <button
                            className="btn btn-outline-secondary mx-2 float-sm-right"
                            data-toggle="modal"
                            data-target={`#${editFieldModalId}`}
                            onClick={() => this.handleEditField(field, section.id)}
                            title={this.props.intl.formatMessage(this.messages.editField)}>
                            <i className="fas fa-edit" />
                        </button>
                    </div>
                }
            </div>
        );
    };

    renderSelected = () => {
        const { selected } = this.state;
        return selected.map(section => {
            const removable = section.fields.findIndex(x => x.fixed === true) === -1
            return (
                <div className="p-3" key={section.id}>
                    <div className="font-weight-bold">
                        {section.name} ({section.fields.length})
                        {removable && <button
                            onClick={() => this.handleRemoveSelectedSection(section.id)}
                            className="btn btn-outline-danger ml-3"
                            title={this.props.intl.formatMessage(this.messages.deleteSection)}>
                            <i className="fas fa-trash-alt" />
                        </button>
                        }
                    </div>

                    <div className="selectfieldSecdTb">
                        {section.fields.map((field, i) =>
                            this.renderSelectedField(i, field, section)
                        )}
                    </div>
                </div>
            );
        });
    };

    renderFieldsForCurrentSection = () => {
        if (this.state.currentSectionIndex === null) return null;
        const section = this.state.sections[this.state.currentSectionIndex];
        return section.fields.map(this.renderField);
    };

    renderField = (field: IDatabase.IField, index: number) => {
        const selected = this.state.selected.findIndex(section => section.fields.findIndex(x => x.id === field.id) !== -1) !== -1;
        return (
            <li key={`field-${index}`}>
                <span
                    className="p-3"
                    onClick={() => {
                        if (!selected)
                            this.handleFieldSelected(field);
                    }}>
                    {field.label}
                    {selected && <span className="float-right text-success">
                        <i className="fas fa-check" />
                    </span>
                    }
                </span>
            </li>
        );
    };

    render() {
        return (
            <div>
                <div className="row px-2">
                    <h5><FormattedMessage id="db.fields.hdr" defaultMessage="2. Database Fields" /></h5>
                </div>
                <div className="row text-muted px-2 py-3">
                    <FormattedHTMLMessage id="db.fields.intro" defaultMessage={`
<p>In this section, you will choose fields that determine what information your partners will enter regarding their organizations or resources, and what information will be viewed by your end users.</p>
<p>Add fields to your database by clicking on the field name on the left. You can select from our available standard fields or create your own by using the <strong>Add Custom Field</strong> button. Each field is fully customizable and can be made mandatory or optional for your partners to fill out.</p>
<p>The fields that will be included in your database will appear on the right.</p>
<p>Tip:  Please think carefully about which fields you would like, as it can cause additional work for your partners in the future if you modify your fields after your partners have begun creating Information Pages for your database.</p>
                    `} />
                </div>

                <div className="row">
                    <div className="col-12 col-md-6 d-flex-row border-bottom align-self-stretch">
                        <div className="row border">
                            <FormattedMessage id="db.fields.options.hdr" defaultMessage="Available Options">
                                {(txt) => (<h5 className="p-3">{txt}</h5>)}
                            </FormattedMessage>
                        </div>
                        <div className="row  d-flex-row align-self-stretch">
                            <div className="col-6 col-md-6 dbFieldLt p-0 dark-grey-bg d-flex-row align-self-stretch">
                                <div className="p-3 font-weight-bold">
                                    <FormattedMessage id="db.fields.sections.hdr" defaultMessage="Sections" />
                                    <i className="fas fa-question-circle hmzTooltip" data-toggle="tooltip" data-placement="top" title={this.props.intl.formatMessage(this.messages.sectionHelp)} />
                                </div>
                                <ul>{this.state.sections.map(this.renderDefaultSection)}</ul>
                                <div className="p-3 font-weight-bold"><FormattedMessage id="db.fields.sections.custom" defaultMessage="Custom Sections" /></div>
                                <ul>{this.state.sections.map(this.renderCustomSection)}</ul>
                                <button
                                    data-toggle="modal"
                                    data-target={`#${customSectionModalId}`}
                                    className="harmonize m-3 px-3 py-1 align-self-center">
                                    <FormattedMessage id="db.fields.sections.add.btn" defaultMessage="Add Custom Section" />
                                </button>
                            </div>
                            <div className="col-6 dbFieldLt p-0 d-flex-row align-self-stretch">
                                <div className="p-3 font-weight-bold">
                                    <FormattedMessage id="db.fields.fields.hdr" defaultMessage="Fields" />
                                    <i className="fas fa-question-circle hmzTooltip" data-toggle="tooltip" data-placement="top" title={this.props.intl.formatMessage(this.messages.fieldsHelp)} />
                                </div>
                                <ul>{this.renderFieldsForCurrentSection()}</ul>
                                <hr />
                                <button
                                    onClick={() => this.setState({ edit: null })}
                                    data-toggle="modal"
                                    data-target={`#${customFieldModalId}`}
                                    className="harmonize m-3 px-3 py-1 align-self-center">
                                    <FormattedMessage id="db.fields.field.add.btn" defaultMessage="Add Custom Field" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-6 bg-white d-flex-row border-bottom align-self-stretch">
                        <div className="row">
                            <h5 className="p-3"><FormattedMessage id="db.fields.selected.hdr" defaultMessage="Selected Fields" /></h5>
                        </div>
                        {this.renderSelected()}
                    </div>
                </div>
                <div className="row m-3">
                    {this.props.renderNextStepLink(this.handleSave)}
                    <button className="primary-inv" onClick={this.handleGoBack}>
                        <FormattedMessage id="db.fields.back.btn" defaultMessage="Back" />
                    </button>
                    {!this.state.saved ?
                        <button className="btn btn-link" onClick={this.handleSave}>
                            <FormattedMessage id="db.fields.save.btn" defaultMessage="Save For Later" />
                        </button> : <i className="fas fa-check m-4 pt-2" style={{ color: 'green' }} />
                    }

                </div>
                <CreateField modalId={customFieldModalId} onSave={this.addField} sectionName={this.state.sections[this.state.currentSectionIndex].name} />
                <CreateField
                    modalId={editFieldModalId}
                    onSave={this.editField}
                    field={this.state.edit && this.state.edit.field}
                    sectionName={this.state.sections[this.state.currentSectionIndex].name}
                />
                <FormattedMessage id="db.create.section.modal.hdr" defaultMessage="Add Custom Section">
                    {(txt: string) => (<CreateSection title={txt} modalId={customSectionModalId} onSave={this.addSection} />)}
                </FormattedMessage>

            </div>
        );
    }
}
export default injectIntl(Fields);


// WEBPACK FOOTER //
// ./src/client/database/setup/Fields.tsx